import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { FinancialHistoryChange, FinancialHistoryEvent, FinancialHistoryService, HistoryValue } from './financial-history.service';

interface HistoryGroup {
  key: string;
  actor: string;
  verified: boolean;
  changedAt: string;
  source: string;
  events: FinancialHistoryEvent[];
}

@Component({
  selector: 'app-financial-history',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './financial-history.component.html',
  styleUrls: ['./financial-history.component.scss'],
})
export class FinancialHistoryComponent implements OnChanges, OnDestroy {
  @Input() ladingUuid: string | null = null;
  @Input() active = true;
  loading = false;
  error = '';
  coverage = '';
  groups: HistoryGroup[] = [];
  nextCursor: string | null = null;
  private events: FinancialHistoryEvent[] = [];
  private request?: Subscription;
  private sequence = 0;

  constructor(private readonly history: FinancialHistoryService) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.cancel();
    if (changes['ladingUuid']) {
      this.events = [];
      this.groups = [];
      this.nextCursor = null;
      this.error = '';
    }
    if (this.active && this.ladingUuid) this.load(true);
  }

  refresh(): void { this.load(true); }
  older(): void { if (this.nextCursor && !this.loading) this.load(false); }

  private load(reset: boolean): void {
    if (!this.ladingUuid || !this.active) return;
    this.cancel();
    const sequence = this.sequence;
    this.loading = true;
    this.error = '';
    if (reset) { this.events = []; this.groups = []; this.nextCursor = null; }
    this.request = this.history.load(this.ladingUuid, reset ? null : this.nextCursor).subscribe({
      next: page => {
        if (sequence !== this.sequence) return;
        if (!page.ok) { this.error = 'Financial history could not be loaded.'; this.loading = false; return; }
        const known = new Set(this.events.map(event => event.id));
        this.events.push(...page.events.filter(event => !known.has(event.id)));
        this.nextCursor = page.next_cursor;
        this.coverage = page.coverage;
        this.groups = this.group(this.events);
        this.loading = false;
        console.log(this.groups)
      },
      error: () => {
        if (sequence !== this.sequence) return;
        this.error = 'Financial history could not be loaded. Refresh to try again.';
        this.loading = false;
      },
    });
  }

  private group(events: FinancialHistoryEvent[]): HistoryGroup[] {
    const groups = new Map<string, HistoryGroup>();
    for (const event of events) {
      const key = [event.operation_id || event.request_id || event.id, event.actor.label, event.actor.verified, event.source].join(':');
      let group = groups.get(key);
      if (!group) {
        group = { key, actor: event.actor.label, verified: event.actor.verified, changedAt: event.changed_at, source: event.source, events: [] };
        groups.set(key, group);
      }
      group.events.push(event);
    }
    return [...groups.values()];
  }

  mainGroupEvent(events: FinancialHistoryEvent[]): FinancialHistoryEvent[] | null {
    let mainEventEntityPriority: Record<string, number> = {
      'Settlement': 3,
      'Customer line': 1,
      'Carrier line': 1,
      'Customer charge': 2,
      'Carrier cost': 2
    }
    let highestPriorityNumber: number = 100;
    let mainEvents: FinancialHistoryEvent[] = [];
    for (const event of events) {
      if (event.entity in mainEventEntityPriority && mainEventEntityPriority[event.entity] < highestPriorityNumber) {
        highestPriorityNumber = mainEventEntityPriority[event.entity];
      }
    }
    for (const event of events) {
      if (event.entity in mainEventEntityPriority && mainEventEntityPriority[event.entity] === highestPriorityNumber) {
        mainEvents.push(event);
      }
    }
    return mainEvents;
  }

  action(event: FinancialHistoryEvent): string {
    return event.operation === 'INSERT' ? 'Added' : event.operation === 'DELETE' ? 'Deleted' : 'Changed';
  }

  value(value: HistoryValue, change: FinancialHistoryChange): string {
    if (value === null) return '—';
    if (change.kind === 'money' && Number.isFinite(Number(value))) return (Number(value) / 100).toFixed(2);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  trackGroup(_index: number, group: HistoryGroup): string { return group.key; }
  trackEvent(_index: number, event: FinancialHistoryEvent): string { return event.id; }

  private cancel(): void {
    this.sequence++;
    this.request?.unsubscribe();
    this.loading = false;
  }

  ngOnDestroy(): void { this.cancel(); }
}
